import type { PropsWithChildren } from 'react'
import { buildImgSrc } from '@/helpers/image-url'
import { LazyImage } from '@/scopes/common/components/Image'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import * as Dialog from '@radix-ui/react-dialog'
import { Modal, mqMin, space, tok, tokPx, zIndex } from '@sunwebgroup/ui-components'
import { Button, Icon, Typography } from '@sunwebgroup/ui-components/uds'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { styled } from 'styled-components'

const StyledModalHeader = styled(Modal.Header)`
  background: ${tok('primitives_color_neutral_1000')};
  color: ${tok('primitives_color_neutral_0')};
  height: ${space('primitives_space_64')};

  * {
    color: inherit;
  }
`

const StyledButton = styled(Button)`
  display: none;

  @media (min-width: ${tokPx('primitives_breakpoint_l_min')}) {
    display: inline-flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`

const StyledLeftButton = styled(StyledButton)`
  left: 16px;
  width: 48px;
  height: 48px;
  background: ${tok('primitives_color_neutral_0')};
  &:hover {
    border: 1px solid ${tok('primitives_color_neutral_1000')}
  }
  // This is because the icon is causing wrong target for Google Analytics to be tracked
  ${Icon} {
    pointer-events: none;
  }
`

const StyledRightButton = styled(StyledButton)`
  right: 16px;
  width: 48px;
  height: 48px;
  background: ${tok('primitives_color_neutral_0')};
  &:hover {
    border: 1px solid ${tok('primitives_color_neutral_1000')}
  }
  // This is because the icon is causing wrong target for Google Analytics to be tracked
  ${Icon} {
    pointer-events: none;
  }
`

const Container = styled.section`
  position: relative;
  height: 100%;
  width: 100%;
  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding: 0 ${space('primitives_space_32')};
  }
  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding: 0 ${space('primitives_space_80')};
  }
`

const Slider = styled.div`
  overflow: hidden;
  height: 100%;
`

const SliderContainer = styled.div`
  display: flex;
  height: 100%;
`

const Slide = styled.div`
  flex: 0 0 100%;

  iframe {
    height: 100%;
    width: 100%;
  }

  img {
    object-fit: contain;
    height: 100%;
    background-color: ${tok('primitives_color_neutral_1000')};
  }
`

const DetailImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
`

const DetailImage = styled(LazyImage)`
  height: 100%;
  object-fit: cover;
  width: 100%;
  filter: unset;
`
const DialogContent = styled(Dialog.Content)`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100vh;
  z-index: ${zIndex.modalOrMobilePopover} !important;
  padding-bottom: 64px;
  background: ${tok('primitives_color_neutral_1000')};
  inset: 0;
  display: flex;
  flex-direction: column;
`

const DetailCloseButton = styled(Button)`
    position: absolute;
    right: ${space('primitives_space_16')};
  &:hover {
    color: unset;
  }
`
export type DetailProps = PropsWithChildren<Omit<Modal.ModalProps, '$size' | 'title'>> & {
  startFromIndex?: number
  roomtypeLabel: string
  roomtypeImages: string[]
}

export function Detail({ startFromIndex = 0, roomtypeLabel, roomtypeImages, open, ...attrs }: DetailProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, startIndex: startFromIndex })
  const { sendEvent } = useAnalytics()
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const numberOfSlides = roomtypeImages.length

  function navigate(direction: 'previous' | 'next') {
    if (direction === 'previous') {
      emblaApi?.scrollPrev()
    }
    else {
      emblaApi?.scrollNext()
    }
  }

  // Slider events
  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap()
      setCurrentSlideIndex(index)
    }
  }, [emblaApi])

  const onSettle = useCallback(() => {
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: 'Roomtype image Gallery',
        result: 'Slide',
        type: roomtypeLabel,
      }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We know that sendClickPdpEvent is not changing
  }, [])

  useEffect(() => {
    if (emblaApi === undefined)
      return

    setCurrentSlideIndex(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    emblaApi.on('settle', onSettle)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('settle', onSettle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run once embla is mounted
  }, [emblaApi])

  const handleClose = () => {
    attrs.onClose?.()
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <DialogContent asChild>
          <div>
            <StyledModalHeader>
              <Typography $variant="body-l-bold-snug">{`${currentSlideIndex + 1}  / ${numberOfSlides}`}</Typography>
              <Dialog.Close asChild>
                <DetailCloseButton $type="transparentNeutral">
                  <Icon glyph="CrossMedium" />
                </DetailCloseButton>
              </Dialog.Close>
            </StyledModalHeader>
            <Container>
              <Slider ref={emblaRef}>
                <SliderContainer>
                  {roomtypeImages.map((image, index) => (
                    <Slide key={index}>
                      <DetailImageWrapper>
                        <DetailImage
                          src={buildImgSrc(image, 480, 270) || ''}
                          srcSet={[480, 240]}
                          alt={roomtypeLabel || ''}
                        />
                      </DetailImageWrapper>
                    </Slide>
                  ))}
                </SliderContainer>
              </Slider>
            </Container>
            {numberOfSlides > 0 && (
              <>
                <StyledLeftButton
                  $type="transparentNeutral"
                  onClick={() => navigate('previous')}
                  disabled={currentSlideIndex === 0}
                >
                  <Icon glyph="ChevronLeft" />
                </StyledLeftButton>
                <StyledRightButton
                  $type="transparentNeutral"
                  onClick={() => navigate('next')}
                  disabled={currentSlideIndex === numberOfSlides - 1}
                >
                  <Icon glyph="ChevronRight" />
                </StyledRightButton>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
